import Vue from 'vue';
import { areRouteParamsEqual } from '@/tools/areRouteParamsEqual';
import { mutationTypes, actionTypes } from '@/types';
import { getUrlVars } from '@/tools/urlVars';
import { setDayjsLocale } from '@/assets/dayjsLocales';

let isScriptLoaded = false;

// To prevent circular dependencies the initialize function takes the
let store;
let router;
export function initializeBeforeEach(_store, _router) {
	store = _store;
	router = _router;
}

export function getDataSourceURL() {
	const path = window.document.location.pathname.split('/');
	const appPath = path.slice(0, path.indexOf('apps'));

	return `${window.document.location.origin}${appPath.join('/')}/`;
}

export function initializeAnalytics(to, from, next) {
	// avoid reloading the script
	if (!isScriptLoaded) {
		isScriptLoaded = true;
		store.dispatch(actionTypes.analyticsInitialize);
	}
	next();
}

export async function identifyHistoryAction(to, from, next) {
	// check if article can be reached by going back or forward in history
	for (const direction of [-1, +1]) {
		// avoid re-validating
		const vueRouterHistoryKey = window.history.state && window.history.state.key;
		const adjacentHistoryEntry = store.state.history.stack[store.state.history.index + direction];
		const validatedParams = { ...to.params };
		if (
			areRouteParamsEqual(adjacentHistoryEntry, validatedParams) &&
			// the keys below are equal when the browser back/forward initiated navigation
			// instead of programmatic navigation
			adjacentHistoryEntry.historyKey !== vueRouterHistoryKey
		) {
			if (
				store.state.dataSourceCFG.exhibitionMap[validatedParams.exhibitionID] &&
				store.state.dataSourceCFG.exhibitionMap[validatedParams.exhibitionID].articleMap[
					validatedParams.articleID
				] &&
				store.state.dataSourceCFG.exhibitionMap[validatedParams.exhibitionID].articleMap[
					validatedParams.articleID
				].type === 'ar-package'
			) {
				next();
				return;
			}
			store.commit(mutationTypes.historyUpdate, adjacentHistoryEntry);
			router.go(direction);
			// cancel this navigation
			next(false);
			return;
		}
	}
	next();
}
let rawQueryRead = false;
export function legacyUrlQuery(to, from, next) {
	// check not hashed URL Vars. If some are find redirect to them

	if (!rawQueryRead) {
		const rawQuery = getUrlVars(false);
		// legacy url variables are set so redirect to new url parameter mode
		if (!rawQuery) return next();
		const path = rawQuery.ex ? `/${rawQuery.ex}${rawQuery.id ? `/${rawQuery.id}` : ''}` : to.path;
		delete rawQuery.ex;
		delete rawQuery.id;
		rawQueryRead = true;
		const query = { ...to.query, ...rawQuery };
		return next({
			path,
			replace: true,
			query,
		});
	}
	next();
}

export async function loadDataSourceCFG(to, from, next) {
	if (!store.state.dataSourceCFG) {
		if (to.query.ds && to.query.ds.includes('preview')) {
			// if datasource is rendered by cms preview login credentials have to be set.
			Vue.http.options.credentials = true;
		}
		if (to.query.language) {
			store.commit(mutationTypes.setLanguage, { language: to.query.language });
		}
		if (to.query.app) {
			store.commit(mutationTypes.setIsApp);
		}
		if (to.query.mac && to.query.mode) {
			store.commit(mutationTypes.setMediaplayer, { mode: 'mediaplayer', mac: to.query.mac });
		}
		if (to.query.isVirtualPackageSyncer) {
			store.commit(mutationTypes.setVirtualPackageSyncer);
		}
		if (to.query.isPreviewDataSource) {
			store.commit(mutationTypes.setIsPreviewDataSource);
		}
		if (to.query.pageTransition === 'swipe') {
			store.commit(mutationTypes.setPageTransitionMode, { transition: 'swipe' });
		}
		// Turn log messages off if isCMSPreview is on or showLogMessages are set to off
		const isCMSPreview = window.location.pathname.includes('preview');
		if (to.query.showLogMessages || isCMSPreview) {
			store.dispatch(
				actionTypes.setShowLogMessages,
				['off', 'false', '0'].includes(to.query.showLogMessages) || isCMSPreview ? false : true
			);
		}

		await store.dispatch(actionTypes.loadDataSource, {
			dataSourceURL: to.query.ds || getDataSourceURL(),
			plugin:
				to.query.pluginName && to.query.plugin
					? { url: to.query.plugin, name: to.query.pluginName }
					: undefined,
		});
		// Set currentLanguage for dayjs
		await setDayjsLocale(store.getters.currentLanguage, store.dispatch);

		if (to.query.css) {
			store.dispatch(actionTypes.setStyleUrl, { styleTemplateURL: to.query.css });

			store.commit(mutationTypes.activeCssMode);
		}
	}
	next();
}
